import { useCallback, useState } from "react";
import apiFetch from "../../utils/apiFetch";
import { authHeaders } from "./helpers";
import { useInvalidatePayRunDetail } from "./usePayRunDetail";

export interface UsePayRunActionsOptions {
  id: string | undefined;
  onFinished?: () => void;
  onNavigateAway?: () => void;
}

export function usePayRunActions({
  id,
  onFinished,
  onNavigateAway,
}: UsePayRunActionsOptions) {
  const [processing, setProcessing] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const invalidate = useInvalidatePayRunDetail(id);

  const runAction = useCallback(
    async (path: string, method: "POST" | "DELETE", navigateAfter: boolean) => {
      if (!id) return;
      setProcessing(true);
      setActionError(null);
      try {
        const res = await apiFetch("/api/v1/payroll/runs/" + id + path, {
          method,
          headers: authHeaders(),
        });
        if (!res.ok) {
          const errBody = await res.json().catch(() => ({}));
          throw new Error(
            errBody.detail || "Action failed (HTTP " + res.status + ")"
          );
        }
        onFinished?.();
        if (navigateAfter) {
          onNavigateAway?.();
        } else {
          invalidate();
        }
      } catch (err) {
        setActionError(err instanceof Error ? err.message : "Unknown error");
      } finally {
        setProcessing(false);
      }
    },
    [id, invalidate, onFinished, onNavigateAway]
  );

  const finalize = useCallback(
    () => runAction("/finalize", "POST", false),
    [runAction]
  );
  const voidRun = useCallback(
    () => runAction("/void", "POST", false),
    [runAction]
  );
  const deleteRun = useCallback(
    () => runAction("", "DELETE", true),
    [runAction]
  );

  const resetActionError = useCallback(() => setActionError(null), []);

  return {
    processing,
    actionError,
    resetActionError,
    finalize,
    voidRun,
    deleteRun,
  };
}